import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { ArrowRight, ArrowLeft, CheckCircle, XCircle, CircleNotch, Eye, EyeSlash, Car, Motorcycle, Phone, Warning } from "@phosphor-icons/react";
import { isLimitedQuotaWindow, SUPPORT_PHONE, SUPPORT_PHONE_TEL } from "@/lib/enrollmentCycle";

const EDUCATION_OPTIONS = [
  { v: "okuryazar", l: "Okur-yazar / Okuma-yazma belgesi" },
  { v: "ilkokul", l: "İlkokul mezunu" },
  { v: "ortaokul", l: "Ortaokul mezunu" },
  { v: "lise-ogrenci", l: "Lise öğrencisi" },
  { v: "lise", l: "Lise mezunu" },
  { v: "onlisans-ogrenci", l: "Ön Lisans öğrencisi" },
  { v: "onlisans", l: "Ön Lisans mezunu" },
  { v: "lisans-ogrenci", l: "Üniversite öğrencisi" },
  { v: "lisans", l: "Üniversite mezunu" },
  { v: "yukseklisans", l: "Yüksek lisans / Doktora" },
];

const passwordStrength = (pw) => {
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};

export default function SignupWizard() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [showPw, setShowPw] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tcStatus, setTcStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [f, setF] = useState({
    email: "", password: "", password_confirm: "",
    ad: "", soyad: "", tc_no: "", birth_date: "",
    education: "lise", phone: "", phone_confirm: "",
    course_class: "B", has_prior: false, prior_class: "",
  });

  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data)).catch(()=>{});
    // Oturum açık kullanıcı 1. adımı atlar
    if (user && step === 1) {
      setF(prev => ({ ...prev, email: user.email || "" }));
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Live TC verification (step 2)
  useEffect(() => {
    setTcStatus("idle");
    if (f.tc_no.length !== 11 || !f.ad.trim() || !f.soyad.trim() || !f.birth_date) return;
    const t = setTimeout(async () => {
      setTcStatus("checking");
      try {
        const { data } = await api.post("/verify-tc", { tc_no: f.tc_no, ad: f.ad, soyad: f.soyad, dogum_yili: parseInt(f.birth_date.split("-")[0]) });
        if (!data.valid) { setTcStatus("invalid"); return; }
        setTcStatus(data.source === "nvi" ? "valid" : "valid_local");
      } catch { setTcStatus("error"); }
    }, 700);
    return () => clearTimeout(t);
  }, [f.tc_no, f.ad, f.soyad, f.birth_date]);

  const pwScore = passwordStrength(f.password);
  const pwStrong = pwScore >= 3 && /[A-Z]/.test(f.password) && f.password.length >= 6;

  const validateStep1 = () => {
    if (!f.email) return "E-posta adresi zorunlu";
    if (!f.password || !f.password_confirm) return "Şifre ve şifre tekrarı zorunlu";
    if (f.password !== f.password_confirm) return "Şifreler eşleşmiyor";
    if (!pwStrong) return "Şifre en az 6 karakter ve 1 büyük harf içermeli";
    return null;
  };
  const validateStep2 = () => {
    if (!f.ad.trim() || !f.soyad.trim()) return "Ad ve soyad zorunlu";
    if (f.tc_no.length !== 11) return "TC 11 haneli olmalı";
    if (tcStatus === "invalid") return "TC numarası geçersiz (algoritma kontrolü başarısız)";
    if (!f.birth_date) return "Doğum tarihi zorunlu";
    if (!f.phone || f.phone !== f.phone_confirm) return "Telefon numaraları eşleşmiyor";
    if (!f.education) return "Öğrenim durumu seçin";
    return null;
  };

  const goNext = () => {
    setErr("");
    const e = step === 1 ? validateStep1() : validateStep2();
    if (e) { setErr(e); return; }
    if (step === 2) {
      // 5 saniyelik kontrol animasyonu
      setChecking(true);
      setTimeout(() => { setChecking(false); setStep(3); }, 5500);
    } else {
      setStep(step + 1);
    }
  };

  const submit = async () => {
    setLoading(true); setErr("");
    try {
      // 1) Hesap yoksa oluştur (oturum açıksa atla)
      if (!user) {
        await register({
          email: f.email, password: f.password,
          full_name: `${f.ad} ${f.soyad}`, phone: f.phone,
        });
      }
      // 2) Başvuru oluştur
      const { data: reg } = await api.post("/registrations", {
        tc_no: f.tc_no, full_name: `${f.ad} ${f.soyad}`, phone: f.phone, email: f.email || user?.email,
        birth_date: f.birth_date, address: "Sonra girilecek", course_class: f.course_class,
        education_level: f.education, notes: f.has_prior ? `Önceki sınıf: ${f.prior_class}` : "",
        payment_method: "office",
      });
      nav(`/basvurum/${reg.id}`);
    } catch (e) { setErr(e.response?.data?.detail || "Kayıt oluşturulamadı"); }
    finally { setLoading(false); }
  };

  const pct = Math.round((step / 3) * 100);

  return (
    <>
      <SEO title="Kayıt Ol | Estem Sürücü Kursu" canonical="https://www.estemsurucukursu.com/basla"/>
      <section className="px-4 md:px-8 py-10 md:py-14 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-primary mb-2">Ehliyet Yolculuğu</div>
          <h1 className="font-heading font-bold text-3xl md:text-5xl tracking-tight leading-[1.1]">
            Ehliyet macerana <span className="font-serif italic font-normal text-brand-primary">sadece birkaç adım</span> kaldı
          </h1>
          <p className="mt-3 text-stone-600">3 dakika içinde başvurunu başlat, evraklarını yükle, dersine başla.</p>
        </div>

        <div className="flex items-center gap-2 mb-6">
          {[1,2,3].map(n => (
            <div key={n} className="flex items-center flex-1">
              <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center font-bold text-sm shrink-0 ${step >= n ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-stone-400 border-stone-300"}`}>
                {step > n ? <CheckCircle weight="fill" size={18}/> : n}
              </div>
              {n < 3 && <div className={`h-1 flex-1 mx-1 rounded-full ${step > n ? "bg-brand-primary" : "bg-stone-200"}`}/>}
            </div>
          ))}
        </div>
        <div className="text-xs text-stone-500 text-center mb-6">Adım {step}/3 · %{pct} tamamlandı</div>

        {err && <div className="mb-4 p-3 bg-brand-error/10 border border-brand-error/30 rounded-lg text-brand-error font-semibold text-sm">{err}</div>}

        {checking && (
          <div className="nb-card p-10 text-center" data-testid="checking-anim">
            <CircleNotch weight="bold" size={56} className="text-brand-primary animate-spin mx-auto"/>
            <div className="mt-6 font-heading font-bold text-xl">Bilgileriniz kontrol ediliyor…</div>
            <div className="mt-4 space-y-2 text-sm text-stone-600">
              <div className="animate-pulse">✓ Yaş standartları uygunluk kontrolü</div>
              <div className="animate-pulse" style={{animationDelay:'0.5s'}}>✓ NVI kimlik doğrulaması</div>
              <div className="animate-pulse" style={{animationDelay:'1s'}}>✓ Öğrenim durumu değerlendirmesi</div>
              <div className="animate-pulse" style={{animationDelay:'1.5s'}}>✓ Kontenjan sorgusu</div>
              <div className="animate-pulse" style={{animationDelay:'2s'}}>✓ Sınıf uyumluluğu</div>
            </div>
          </div>
        )}

        {!checking && step === 1 && (
          <div className="nb-card p-6 md:p-8" data-testid="signup-step-1">
            <div className="font-heading font-bold text-xl mb-1">1. Adım: Hesap Oluştur</div>
            <div className="text-sm text-stone-500 mb-6">Süreci takip etmek için önce bir hesap açalım.</div>
            <div className="space-y-4">
              <div><label className="nb-label">E-posta *</label><input type="email" value={f.email} onChange={e=>setF({...f, email:e.target.value})} className="nb-input mt-1" data-testid="signup-email"/></div>
              <div>
                <label className="nb-label">Şifre * (en az 6 karakter, 1 büyük harf)</label>
                <div className="relative mt-1">
                  <input type={showPw ? "text" : "password"} value={f.password} onChange={e=>setF({...f, password:e.target.value})} className="nb-input pr-10" data-testid="signup-password"/>
                  <button type="button" onClick={()=>setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">{showPw ? <EyeSlash weight="bold" size={18}/> : <Eye weight="bold" size={18}/>}</button>
                </div>
                {f.password && (
                  <div className="mt-2">
                    <div className="flex gap-1 h-1">
                      {[1,2,3,4,5].map(i => <div key={i} className={`flex-1 rounded ${i <= pwScore ? (pwScore < 3 ? "bg-brand-error" : pwScore < 4 ? "bg-orange-500" : "bg-brand-success") : "bg-stone-200"}`}/>)}
                    </div>
                    <div className="text-xs mt-1 text-stone-500">{pwScore < 3 ? "Zayıf" : pwScore < 4 ? "Orta" : "Güçlü"} şifre</div>
                  </div>
                )}
              </div>
              <div>
                <label className="nb-label">Şifre Tekrar *</label>
                <input type={showPw ? "text" : "password"} value={f.password_confirm} onChange={e=>setF({...f, password_confirm:e.target.value})} className="nb-input mt-1" data-testid="signup-password-confirm"/>
                {f.password_confirm && f.password !== f.password_confirm && <div className="text-xs text-brand-error mt-1">Şifreler eşleşmiyor</div>}
                {f.password_confirm && f.password === f.password_confirm && f.password_confirm.length > 0 && <div className="text-xs text-brand-success mt-1">✓ Şifreler eşleşiyor</div>}
              </div>
            </div>
            <button onClick={goNext} className="nb-btn w-full mt-6" data-testid="signup-next-1">Devam Et <ArrowRight weight="bold"/></button>
          </div>
        )}

        {!checking && step === 2 && (
          <div className="nb-card p-6 md:p-8" data-testid="signup-step-2">
            <div className="font-heading font-bold text-xl mb-1">2. Adım: Kişisel Bilgiler</div>
            <div className="text-sm text-stone-500 mb-6">Ehliyet başvurunuz için resmi bilgileriniz.</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="nb-label">Ad *</label><input value={f.ad} onChange={e=>setF({...f, ad:e.target.value})} className="nb-input mt-1" data-testid="signup-ad"/></div>
              <div><label className="nb-label">Soyad *</label><input value={f.soyad} onChange={e=>setF({...f, soyad:e.target.value})} className="nb-input mt-1" data-testid="signup-soyad"/></div>
              <div className="md:col-span-2">
                <label className="nb-label">T.C. Kimlik No *</label>
                <div className="relative mt-1">
                  <input maxLength={11} value={f.tc_no} onChange={e=>setF({...f, tc_no:e.target.value.replace(/\D/g,'')})} className={`nb-input pr-10 ${tcStatus==="valid"||tcStatus==="valid_local"?"border-brand-success":tcStatus==="invalid"?"border-brand-error":""}`} data-testid="signup-tc"/>
                  {tcStatus === "checking" && <CircleNotch weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-primary animate-spin" size={20}/>}
                  {(tcStatus === "valid" || tcStatus === "valid_local") && <CheckCircle weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-success" size={22}/>}
                  {tcStatus === "invalid" && <XCircle weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-error" size={22}/>}
                </div>
                {tcStatus === "valid" && <div className="text-xs text-brand-success font-semibold mt-1">✓ NVI ile doğrulandı</div>}
                {tcStatus === "valid_local" && <div className="text-xs text-brand-success font-semibold mt-1">✓ T.C. kimlik algoritması doğrulandı (NVI kamu servisi 30.09.2025&apos;ten itibaren kapalı; ofiste evrak kontrolü yapılacaktır)</div>}
                {tcStatus === "invalid" && <div className="text-xs text-brand-error font-semibold mt-1">TC numarası geçersiz. Lütfen kontrol edin.</div>}
              </div>
              <div><label className="nb-label">Doğum Tarihi *</label><input type="date" value={f.birth_date} onChange={e=>setF({...f, birth_date:e.target.value})} className="nb-input mt-1" data-testid="signup-birth"/></div>
              <div>
                <label className="nb-label">Öğrenim Durumu *</label>
                <select value={f.education} onChange={e=>setF({...f, education:e.target.value})} className="nb-input mt-1" data-testid="signup-education">
                  {EDUCATION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div><label className="nb-label">Telefon *</label><input type="tel" value={f.phone} onChange={e=>setF({...f, phone:e.target.value.replace(/\D/g,'')})} className="nb-input mt-1" data-testid="signup-phone" placeholder="05XXXXXXXXX"/></div>
              <div>
                <label className="nb-label">Telefon (tekrar) *</label>
                <input type="tel" value={f.phone_confirm} onChange={e=>setF({...f, phone_confirm:e.target.value.replace(/\D/g,'')})} className="nb-input mt-1" data-testid="signup-phone-confirm" placeholder="Aynı numarayı tekrar girin"/>
                {f.phone_confirm && f.phone !== f.phone_confirm && <div className="text-xs text-brand-error mt-1">Eşleşmiyor</div>}
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={()=>setStep(1)} className="nb-btn-outline" data-testid="signup-back-2"><ArrowLeft weight="bold"/> Geri</button>
              <button onClick={goNext} className="nb-btn flex-1" data-testid="signup-next-2">Devam Et <ArrowRight weight="bold"/></button>
            </div>
          </div>
        )}

        {!checking && step === 3 && (
          <div className="nb-card p-6 md:p-8" data-testid="signup-step-3">
            <div className="font-heading font-bold text-xl mb-1">3. Adım: Ehliyet Sınıfı Seçin</div>
            <div className="text-sm text-stone-500 mb-6">Hangi sınıf ehliyet almak istiyorsunuz?</div>
            <div className="grid md:grid-cols-2 gap-3">
              {courses.map(c => {
                const Icon = c.icon === "Motorcycle" ? Motorcycle : Car;
                return (
                  <label key={c.code} className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${f.course_class === c.code ? "border-brand-primary bg-brand-primarySubtle" : "border-stone-200 bg-white hover:border-stone-300"}`} data-testid={`signup-class-${c.code}`}>
                    <input type="radio" name="cls" value={c.code} checked={f.course_class === c.code} onChange={e=>setF({...f, course_class:e.target.value})} className="sr-only"/>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon size={28} weight="bold" className={f.course_class === c.code ? "text-brand-primary" : "text-stone-500"}/>
                        <div className="font-bold">{c.name}</div>
                      </div>
                      <div className="font-heading font-bold text-lg text-brand-primary">{c.price.toLocaleString("tr-TR")} ₺</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="mt-6 p-4 border border-stone-200 rounded-xl bg-stone-50">
              <label className="flex items-center gap-2 font-semibold">
                <input type="checkbox" checked={f.has_prior} onChange={e=>setF({...f, has_prior:e.target.checked, prior_class:""})} className="w-4 h-4 accent-brand-primary" data-testid="signup-has-prior"/>
                <span>Daha önce ehliyet aldım</span>
              </label>
              {f.has_prior && (
                <select value={f.prior_class} onChange={e=>setF({...f, prior_class:e.target.value})} className="nb-input mt-3" data-testid="signup-prior-class">
                  <option value="">Mevcut sınıfınızı seçin</option>
                  {courses.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={()=>setStep(2)} className="nb-btn-outline" data-testid="signup-back-3"><ArrowLeft weight="bold"/> Geri</button>
              <button disabled={loading} onClick={submit} className="nb-btn flex-1" data-testid="signup-submit">{loading ? "Kaydınız oluşturuluyor…" : "Kaydımı Oluştur"} <ArrowRight weight="bold"/></button>
            </div>

            {isLimitedQuotaWindow() && (
              <div className="mt-4 p-4 rounded-xl border-2 border-red-300 bg-red-50" data-testid="signup-limited-quota-warning">
                <div className="flex items-center gap-2 mb-1.5">
                  <Warning weight="fill" size={17} className="text-red-500 shrink-0"/>
                  <span className="font-bold text-sm text-brand-ink">Sınırlı Kontenjan Uyarısı</span>
                </div>
                <p className="text-xs text-stone-600 mb-3">Bu dönem online kayıtlarımız şu an değerlendirme aşamasında. Kaydınızı normal şekilde oluşturabilirsiniz, ancak yerinizi garantilemek için bizi hemen aramanızı öneririz.</p>
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm bg-red-500 text-white hover:bg-red-600 transition-colors">
                  <Phone weight="fill" size={15}/> {SUPPORT_PHONE}
                </a>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-center text-sm text-stone-500">Hesabınız var mı? <Link to="/giris" className="font-semibold text-brand-primary">Giriş Yap</Link></div>
      </section>
    </>
  );
}
